import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { USE_MOCK_AUTH } from "../config/api";
import { useLiveEventTick } from "../hooks/useLiveRefresh";
import {
  MOCK_PORTFOLIO_GROUPS as INITIAL_GROUPS,
  generateUploadRecords,
} from "../data/mock/dashboardData";
import {
  createPortfolioGroup,
  deletePortfolioGroup,
  enrichPortfolioGroup,
  fetchAllPortfolioGroups,
  fetchPortfolioGroup,
  normalizePortfolioGroup,
  updatePortfolioGroup,
  clearPortfolioGroupsCache,
} from "../services/portfolioGroupsApi";
import {
  attachUploadsToGroups,
  fetchPortfolioFileUploads,
  uploadPortfolioFile,
  clearPortfolioUploadsCache,
} from "../services/portfolioUploadsApi";

const PortfolioContext = createContext(null);

function summarizeRecords(records, upload) {
  const totalOwed = records.reduce((sum, row) => sum + (Number(row.amount_owed) || 0), 0);
  return {
    ...upload,
    records,
    accounts: records.length,
    totalOwed,
    casesCreated:
      upload.stage === "cases_created" ? records.length : upload.casesCreated ?? 0,
  };
}

function matchGroup(group, groupId) {
  if (!group || groupId == null) return false;
  const id = String(groupId);
  return (
    String(group.id) === id ||
    String(group.group_slug || "") === id ||
    String(group.numericId || "") === id
  );
}

function groupSlugOf(group, fallbackId) {
  return group?.group_slug || group?.id || fallbackId;
}

async function loadGroupsWithUploads(token, { force = false } = {}) {
  const [list, uploadsResult] = await Promise.all([
    fetchAllPortfolioGroups(token, { force }),
    fetchPortfolioFileUploads(token, { force }).then(
      (uploads) => ({ uploads: uploads || [], error: null }),
      (err) => ({ uploads: [], error: err })
    ),
  ]);
  const merged = attachUploadsToGroups(list, uploadsResult.uploads).map(enrichPortfolioGroup);
  return {
    groups: merged,
    uploadsError: uploadsResult.error?.message || null,
  };
}

/** Keep optimistically added groups when a stale fetch completes after create. */
function mergeFetchedGroups(fetched, current) {
  if (!current?.length) return fetched;
  const fetchedIds = new Set(fetched.map((group) => group.id).filter(Boolean));
  const localOnly = current.filter((group) => group?.id && !fetchedIds.has(group.id));
  if (!localOnly.length) return fetched;
  return [...localOnly, ...fetched];
}

export function PortfolioProvider({ children }) {
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const livePortfolioTick = useLiveEventTick(/^(portfolio\.|case\.|payment\.)/);

  const [groups, setGroups] = useState(() => (useLiveApi ? [] : INITIAL_GROUPS));
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState(null);
  const [uploadsError, setUploadsError] = useState(null);
  const loadGenRef = useRef(0);

  const invalidateInFlightLoad = useCallback(() => {
    loadGenRef.current += 1;
  }, []);

  const refreshGroups = useCallback(async () => {
    if (!useLiveApi) {
      setGroups((prev) => (prev.length ? prev : INITIAL_GROUPS));
      return;
    }

    invalidateInFlightLoad();
    clearPortfolioGroupsCache();
    clearPortfolioUploadsCache();
    setGroupsLoading(true);
    setGroupsError(null);
    setUploadsError(null);
    try {
      const { groups: list, uploadsError: uploadErr } = await loadGroupsWithUploads(token, {
        force: true,
      });
      setGroups(list);
      setUploadsError(uploadErr);
    } catch (err) {
      setGroupsError(err.message || "Could not load portfolio groups.");
      setGroups([]);
      throw err;
    } finally {
      setGroupsLoading(false);
    }
  }, [token, useLiveApi, invalidateInFlightLoad]);

  useEffect(() => {
    let cancelled = false;
    const loadGen = loadGenRef.current;

    async function load() {
      if (!useLiveApi) {
        setGroups(INITIAL_GROUPS);
        setGroupsError(null);
        setUploadsError(null);
        setGroupsLoading(false);
        return;
      }

      const quiet = Boolean(livePortfolioTick);
      if (!quiet) setGroupsLoading(true);
      setGroupsError(null);
      setUploadsError(null);
      try {
        const { groups: list, uploadsError: uploadErr } = await loadGroupsWithUploads(token, {
          force: quiet,
        });
        if (!cancelled && loadGen === loadGenRef.current) {
          setGroups((prev) => mergeFetchedGroups(list, prev));
          setUploadsError(uploadErr);
        }
      } catch (err) {
        if (!cancelled) {
          setGroupsError(err.message || "Could not load portfolio groups.");
          setGroups([]);
        }
      } finally {
        if (!cancelled) setGroupsLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token, useLiveApi, livePortfolioTick]);

  const addGroup = useCallback(
    async (group) => {
      if (!useLiveApi) {
        const id = `GRP-${String(Date.now()).slice(-6)}`;
        const next = {
          id,
          createdAt: new Date().toISOString().slice(0, 10),
          uploads: [],
          numberOfCases: 0,
          ...group,
        };
        setGroups((prev) => [next, ...prev]);
        return id;
      }

      invalidateInFlightLoad();
      clearPortfolioGroupsCache();

      const created = await createPortfolioGroup(token, group);
      if (!created?.id) {
        throw new Error("Portfolio group was created but the server returned incomplete data.");
      }

      const normalized = { uploads: [], ...created };
      setGroups((prev) => [
        normalized,
        ...prev.filter((item) => item.id !== normalized.id),
      ]);
      return normalized.id;
    },
    [token, useLiveApi, invalidateInFlightLoad]
  );

  const updateGroup = useCallback(
    async (groupId, patch) => {
      const current = groups.find((item) => matchGroup(item, groupId));
      if (!useLiveApi) {
        setGroups((prev) =>
          prev.map((item) =>
            matchGroup(item, groupId)
              ? {
                  ...item,
                  ...patch,
                }
              : item
          )
        );
        return groupId;
      }

      invalidateInFlightLoad();
      clearPortfolioGroupsCache();

      const slug = groupSlugOf(current, groupId);
      const updated = await updatePortfolioGroup(token, slug, {
        name: patch.name ?? current?.name,
        description: patch.description ?? current?.description,
      });
      setGroups((prev) =>
        prev.map((item) =>
          matchGroup(item, groupId) ? { ...item, ...updated, uploads: item.uploads } : item
        )
      );
      return updated.id;
    },
    [groups, token, useLiveApi, invalidateInFlightLoad]
  );

  const removeGroup = useCallback(
    async (groupId) => {
      if (!useLiveApi) {
        setGroups((prev) => prev.filter((item) => !matchGroup(item, groupId)));
        return true;
      }

      invalidateInFlightLoad();
      clearPortfolioGroupsCache();

      const current = groups.find((item) => matchGroup(item, groupId));
      await deletePortfolioGroup(token, groupSlugOf(current, groupId));
      setGroups((prev) => prev.filter((item) => !matchGroup(item, groupId)));
      return true;
    },
    [groups, token, useLiveApi, invalidateInFlightLoad]
  );

  const addUploadToGroup = useCallback((groupId, upload) => {
    setGroups((prev) =>
      prev.map((g) =>
        matchGroup(g, groupId) ? { ...g, uploads: [upload, ...(g.uploads || [])] } : g
      )
    );
  }, []);

  const uploadFileToGroup = useCallback(
    async (groupId, file) => {
      const current = groups.find((item) => matchGroup(item, groupId));
      const slug = groupSlugOf(current, groupId);

      if (!useLiveApi) {
        return null;
      }

      const { file_upload: upload, portfolio_group: rawGroup } = await uploadPortfolioFile(token, {
        file,
        portfolioGroupSlug: slug,
      });

      if (!upload?.id) {
        throw new Error("Upload succeeded but the server returned no file details.");
      }

      setGroups((prev) =>
        prev.map((item) => {
          if (!matchGroup(item, groupId) && !matchGroup(item, slug)) return item;
          const next = rawGroup
            ? { ...item, ...normalizePortfolioGroup(rawGroup), uploads: item.uploads || [] }
            : item;
          const existing = (next.uploads || []).filter((row) => String(row.id) !== String(upload.id));
          return {
            ...next,
            uploads: [upload, ...existing],
            numberOfCases: rawGroup
              ? Number(rawGroup.number_of_cases ?? next.numberOfCases ?? 0)
              : next.numberOfCases,
          };
        })
      );

      return { upload, group: rawGroup };
    },
    [groups, token, useLiveApi]
  );

  const getGroup = useCallback(
    (groupId) => groups.find((g) => matchGroup(g, groupId)) ?? null,
    [groups]
  );

  const ensureGroup = useCallback(
    async (groupId) => {
      const existing = groups.find((g) => matchGroup(g, groupId));
      if (existing) return existing;
      if (!useLiveApi || !groupId) return null;

      const fetched = enrichPortfolioGroup(await fetchPortfolioGroup(token, groupId));
      if (fetched) {
        setGroups((prev) => {
          if (prev.some((item) => matchGroup(item, fetched.id))) {
            return prev.map((item) => (matchGroup(item, fetched.id) ? { ...item, ...fetched } : item));
          }
          return [fetched, ...prev];
        });
      }
      return fetched;
    },
    [groups, token, useLiveApi]
  );

  const getUpload = useCallback(
    (groupId, uploadId) => {
      const group = getGroup(groupId);
      if (!group) return { group: null, upload: null };
      const upload = group.uploads?.find((u) => String(u.id) === String(uploadId)) ?? null;
      return { group, upload };
    },
    [getGroup]
  );

  /** Materialize generated mock rows into store so edit/delete persist. */
  const ensureUploadRecords = useCallback((groupId, uploadId) => {
    setGroups((prev) => {
      const group = prev.find((item) => matchGroup(item, groupId));
      const upload = group?.uploads.find((item) => String(item.id) === String(uploadId));
      if (!group || !upload || upload.records?.length) return prev;

      return prev.map((item) => {
        if (!matchGroup(item, groupId)) return item;
        return {
          ...item,
          uploads: item.uploads.map((row) =>
            String(row.id) === String(uploadId)
              ? {
                  ...row,
                  records: generateUploadRecords(row.accounts, row.id, item.name),
                }
              : row
          ),
        };
      });
    });
  }, []);

  const updateUploadRecord = useCallback((groupId, uploadId, recordId, patch) => {
    setGroups((prev) =>
      prev.map((group) => {
        if (!matchGroup(group, groupId)) return group;
        return {
          ...group,
          uploads: group.uploads.map((upload) => {
            if (String(upload.id) !== String(uploadId) || !upload.records) return upload;
            const records = upload.records.map((row) => {
              if (row.id !== recordId) return row;
              const next = { ...row, ...patch };
              const owed = Number(
                patch.amount_owed ?? patch.outstanding_amount ?? next.amount_owed ?? 0
              );
              next.amount_owed = owed;
              next.outstanding_amount = owed;
              if (patch.amount_paid != null) next.amount_paid = Number(patch.amount_paid) || 0;
              return next;
            });
            return summarizeRecords(records, upload);
          }),
        };
      })
    );
  }, []);

  const deleteUploadRecord = useCallback((groupId, uploadId, recordId) => {
    setGroups((prev) =>
      prev.map((group) => {
        if (!matchGroup(group, groupId)) return group;
        return {
          ...group,
          uploads: group.uploads.map((upload) => {
            if (String(upload.id) !== String(uploadId) || !upload.records) return upload;
            const records = upload.records.filter((row) => row.id !== recordId);
            return summarizeRecords(records, upload);
          }),
        };
      })
    );
  }, []);

  const value = useMemo(
    () => ({
      groups,
      groupsLoading,
      groupsError,
      uploadsError,
      refreshGroups,
      addGroup,
      updateGroup,
      removeGroup,
      addUploadToGroup,
      uploadFileToGroup,
      getGroup,
      ensureGroup,
      getUpload,
      ensureUploadRecords,
      updateUploadRecord,
      deleteUploadRecord,
      useLiveApi,
    }),
    [
      groups,
      groupsLoading,
      groupsError,
      uploadsError,
      refreshGroups,
      addGroup,
      updateGroup,
      removeGroup,
      addUploadToGroup,
      uploadFileToGroup,
      getGroup,
      ensureGroup,
      getUpload,
      ensureUploadRecords,
      updateUploadRecord,
      deleteUploadRecord,
      useLiveApi,
    ]
  );

  return <PortfolioContext.Provider value={value}>{children}</PortfolioContext.Provider>;
}

export function usePortfolios() {
  const ctx = useContext(PortfolioContext);
  if (!ctx) throw new Error("usePortfolios must be used within PortfolioProvider");
  return ctx;
}
